import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

interface FaqItem {
  question: string;
  answer: string;
}

const faqs: FaqItem[] = [
  {
    question: '¿DonaApp es gratis?',
    answer: 'Sí. Crear campañas, publicar listas de productos y registrar compromisos de donación es completamente gratuito. DonaApp no cobra comisiones ni tarifas a organizadores ni donantes.',
  },
  {
    question: '¿Las donaciones son de dinero o de productos?',
    answer: 'DonaApp se especializa en donaciones en especie, es decir, productos y bienes tangibles (alimentos, ropa, materiales, útiles, etc.). No procesamos pagos monetarios ni transferencias de dinero. Los donantes se comprometen a entregar productos específicos que la campaña necesita.',
  },
  {
    question: '¿Cómo sé que una campaña es legítima?',
    answer: 'Las campañas que aparecen en el Marketplace público pasan por un proceso de verificación manual realizado por nuestro equipo. Las campañas verificadas muestran un badge "✓ Verificada". Además, cada campaña muestra la información de contacto del organizador para que puedas comunicarte directamente.',
  },
  {
    question: '¿Necesito registrarme para donar?',
    answer: 'No. Para comprometerte con una donación solo necesitas proporcionar tu nombre, email y teléfono al momento de registrar tu aporte. El registro con cuenta solo es necesario para crear y administrar campañas.',
  },
  {
    question: '¿Mis datos de contacto son públicos cuando dono?',
    answer: 'No. Tu email y teléfono solo son visibles para el organizador de la campaña, quien los usa exclusivamente para coordinar la entrega. Además, puedes elegir la opción "Donante Anónimo" para que tu nombre tampoco aparezca en la lista pública de la campaña.',
  },
  {
    question: '¿Cómo funciona la entrega de los productos?',
    answer: 'Después de registrar tu compromiso, el organizador de la campaña se pondrá en contacto contigo (o puedes escribirle por WhatsApp) para coordinar la entrega. DonaApp facilita la conexión, pero la logística de entrega se acuerda directamente entre donante y organizador.',
  },
  {
    question: '¿Qué pasa si ya no puedo cumplir mi compromiso?',
    answer: 'Si por alguna razón no puedes entregar los productos que comprometiste, te recomendamos contactar al organizador lo antes posible para que pueda buscar alternativas. El organizador puede actualizar el estado de tu compromiso en la plataforma.',
  },
  {
    question: '¿Cómo creo una campaña?',
    answer: 'Regístrate con tu email, ve a tu Dashboard y haz clic en "Crear Campaña". Define un título, descripción, fecha límite, ubicación, y agrega los productos que necesitas con las cantidades específicas. Puedes compartir tu campaña por enlace privado o solicitar que sea publicada en el Marketplace.',
  },
  {
    question: '¿Mi campaña aparece pública para todos o solo la ven quienes yo decida?',
    answer: 'Tu campaña empieza como privada: compartes el link con quien quieras (WhatsApp, redes sociales, etc.) y solo esas personas pueden verla y donar — este es el modo ideal para la mayoría de personas ayudando a un caso cercano. Si representas una organización con registro legal, puedes solicitar que tu campaña sea verificada y aparezca en el marketplace público, visible para toda la comunidad de DonaApp.',
  },
  {
    question: '¿Puedo crear campañas desde cualquier país?',
    answer: 'Sí. DonaApp está diseñada para funcionar globalmente. Puedes crear campañas desde cualquier país y los donantes pueden comprometerse desde cualquier ubicación, siempre que la coordinación de entrega sea viable entre las partes.',
  },
  {
    question: '¿Qué tipos de campaña puedo crear?',
    answer: 'DonaApp soporta tres tipos: Solidarias (emergencias, comunidades en necesidad), Familiares (baby showers, mudanzas, necesidades del hogar) y Celebraciones (cumpleaños, eventos comunitarios). El tipo de campaña ayuda a los donantes a entender el contexto.',
  },
  {
    question: '¿DonaApp está afiliada a algún gobierno o entidad pública?',
    answer: 'No. DonaApp es una plataforma privada operada por OVIEDOTECH S.A.S. No tenemos afiliación gubernamental ni religiosa. Somos una herramienta tecnológica independiente al servicio de cualquier causa legítima.',
  },
  {
    question: '¿Cómo contacto al equipo de DonaApp?',
    answer: 'Puedes escribirnos a contacto@donaapp.co o llamarnos al +57 316 927 7199. Respondemos en horario laboral de lunes a viernes.',
  },
];

function FaqAccordion({ item }: { item: FaqItem }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-sm text-gray-900 pr-4">{item.question}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`transition-all duration-200 ease-in-out overflow-hidden ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">
          {item.answer}
        </div>
      </div>
    </div>
  );
}

export function FaqPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Preguntas Frecuentes</h1>
      <p className="text-gray-600 mb-8">
        Respuestas a las dudas más comunes sobre DonaApp.
      </p>

      <div className="space-y-3">
        {faqs.map((faq, index) => (
          <FaqAccordion key={index} item={faq} />
        ))}
      </div>

      <div className="mt-10 bg-indigo-50 border border-indigo-200 rounded-lg p-6 text-center">
        <p className="text-sm text-indigo-900 font-medium mb-2">¿No encuentras lo que buscas?</p>
        <p className="text-sm text-indigo-700">
          Escríbenos a{' '}
          <a href="mailto:contacto@donaapp.co" className="font-medium underline">contacto@donaapp.co</a>{' '}
          y te responderemos lo antes posible.
        </p>
      </div>

      <div className="mt-10 pt-6 border-t border-gray-200">
        <Link to="/" className="text-sm text-indigo-600 hover:underline">← Volver al inicio</Link>
      </div>
    </div>
  );
}
